import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Hidden marketing page — the Ad Copy Pack.
 *
 * Loads /api/ads/pack and lays out the 1080×1920 graphic alongside three
 * caption variants with copy-to-clipboard + download buttons.
 */
export default function AdsPage() {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/ads/pack`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setPack(d);
      })
      .catch(() => {
        if (!cancelled) setPack(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Could not copy — select and copy manually"),
    );
  };

  return (
    <div className="min-h-screen relative z-10" data-testid="ads-root">
      <header
        className="sticky top-0 z-20 border-b border-[color:var(--gold)]/15 backdrop-blur-xl"
        style={{ background: "rgba(10, 17, 40, 0.75)" }}
      >
        <div className="mx-auto max-w-[1300px] px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4 group" data-testid="ads-home-link">
            <div className="w-10 h-10 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center group-hover:border-[color:var(--gold)]">
              <Sparkles size={16} className="text-[color:var(--gold)]" />
            </div>
            <div>
              <div className="label-overline text-[color:var(--gold)]/80">EARTH · PULSE</div>
              <div className="font-serif-display text-white text-xl leading-none tracking-tight mt-1">
                launch ad pack
              </div>
            </div>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-6 md:px-12 py-10 md:py-16">
        <section className="mb-14 max-w-3xl">
          <div className="label-overline">Marketing kit</div>
          <h1 className="mt-3 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.03] tracking-tight">
            Launch <span className="font-serif-italic text-[color:var(--gold)]">ad copy</span> pack.
          </h1>
          <p className="mt-5 text-[color:var(--parchment-soft)] text-base md:text-lg font-sans-body leading-relaxed">
            A 1080×1920 TikTok / Reels graphic and three caption variants to test
            (hook, curiosity, gift). Tap any block to copy.
          </p>
        </section>

        {loading && (
          <div className="text-[color:var(--parchment-soft)] font-serif-italic">Loading pack…</div>
        )}

        {pack && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            {/* Graphic column */}
            <div className="md:col-span-5 space-y-8" data-testid="ads-graphic-card">
              {pack.reel_url && (
                <div>
                  <div
                    className="rounded-2xl border border-[color:var(--gold)]/25 overflow-hidden"
                    style={{ background: "rgba(10,17,40,0.5)" }}
                  >
                    <video
                      src={pack.reel_url}
                      poster={pack.graphic_url}
                      controls
                      playsInline
                      loop
                      muted
                      className="w-full block"
                      data-testid="ads-reel-video"
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">
                      {pack.reel_size}
                    </div>
                    <a
                      href={pack.reel_url}
                      download="earth-pulse-tiktok-reel.mp4"
                      data-testid="ads-reel-download"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm"
                    >
                      <Download size={14} /> Download reel
                    </a>
                  </div>
                </div>
              )}
              <div>
                <div
                  className="rounded-2xl border border-[color:var(--gold)]/25 overflow-hidden"
                  style={{ background: "rgba(10,17,40,0.5)" }}
                >
                  <img
                    src={pack.graphic_url}
                    alt="Earth Pulse launch graphic"
                    className="w-full"
                    data-testid="ads-graphic-image"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">
                    {pack.graphic_size} · PNG · print-ready
                  </div>
                  <a
                    href={pack.graphic_url}
                    download="earth-pulse-tiktok-ad.png"
                    data-testid="ads-graphic-download"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--gold)]/50 text-[color:var(--parchment)] font-sans-body text-sm hover:bg-[color:var(--gold)]/10"
                  >
                    <Download size={14} /> Download PNG
                  </a>
                </div>
              </div>
            </div>

            {/* Captions column */}
            <div className="md:col-span-7 space-y-5" data-testid="ads-captions-list">
              {(pack.captions || []).map((c) => {
                const full = `${c.hook}\n\n${c.body}\n\n${(c.hashtags || []).join(" ")}`;
                return (
                  <article
                    key={c.id}
                    data-testid={`ads-caption-${c.id}`}
                    className="rounded-2xl border border-[color:var(--gold)]/20 p-6 md:p-7"
                    style={{ background: "rgba(10, 17, 40, 0.4)" }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="label-overline text-[color:var(--gold)]/85">
                          {c.label}
                        </div>
                        <h3 className="mt-2 font-serif-display text-[color:var(--parchment)] text-2xl leading-tight">
                          {c.hook}
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => copy(full, c.label)}
                        data-testid={`ads-copy-${c.id}`}
                        className="flex-shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[color:var(--gold)]/50 text-[color:var(--parchment)] text-xs font-sans-body hover:bg-[color:var(--gold)]/10"
                      >
                        <Copy size={12} /> Copy all
                      </button>
                    </div>
                    <p className="mt-4 text-[color:var(--parchment-soft)] font-sans-body text-base leading-relaxed whitespace-pre-line">
                      {c.body}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {(c.hashtags || []).map((h) => (
                        <span
                          key={h}
                          className="px-3 py-1 rounded-full border border-[color:var(--gold)]/25 text-[color:var(--gold)] text-xs font-sans-body"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}

        <footer className="mt-16 pt-6 border-t border-[color:var(--gold)]/15 font-serif-italic text-[color:var(--text-secondary)] text-sm">
          Private launch kit · <Link to="/" className="hover:text-[color:var(--gold)]">back to Earth Pulse</Link>
        </footer>
      </main>
    </div>
  );
}
